import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { apiAnonGet, apiGet } from '@/services/api-client';
import { Link } from 'react-router-dom';
import { Users, ChevronLeft, Check, Clock, MapPin, Search } from 'lucide-react';

export default function GroupSearchPage() {
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const [results, setResults] = useState<Array<Record<string, unknown>>>([]);
  const [loading, setLoading] = useState(false);
  const [myGroupIds, setMyGroupIds] = useState<Set<string>>(new Set());
  const [pendingGroupIds, setPendingGroupIds] = useState<Set<string>>(new Set());
  const searchHeadingRef = useRef<HTMLHeadingElement | null>(null);

  const handleSearchFocus = useCallback(() => {
    if (typeof window === 'undefined') return;
    if (!window.matchMedia('(max-width: 768px)').matches) return;

    window.setTimeout(() => {
      const heading = searchHeadingRef.current;
      if (!heading) return;

      const rect = heading.getBoundingClientRect();
      const topOffset = 64;
      const targetTop = Math.max(0, window.scrollY + rect.top - topOffset);

      window.scrollTo({
        top: targetTop,
        behavior: 'smooth',
      });
    }, 120);
  }, []);

  // Fetch user's groups and pending requests on mount
  useEffect(() => {
    (async () => {
      try {
        const res = await apiGet<Array<{ id: string }>>('/groups');
        if (res.success && res.data) {
          const ids = new Set(res.data.map((g) => String(g.id)));
          setMyGroupIds(ids);
        }
      } catch (e) {
        console.error('Failed to fetch user groups', e);
      }

      try {
        const res = await apiGet<{ requests: Array<{ groupId: string; status: string }> }>('/membership-requests/my-requests');
        console.log('[GroupSearchPage] Pending requests response:', res);
        if (res.success && res.data && Array.isArray(res.data.requests)) {
          const pendingIds = new Set(res.data.requests.filter((r) => r.status === 'pending').map((r) => String(r.groupId)));
          console.log('[GroupSearchPage] Pending group IDs:', Array.from(pendingIds));
          setPendingGroupIds(pendingIds);
        }
      } catch (e) {
        console.error('Failed to fetch pending requests', e);
      }
    })();
  }, []);

  const doSearch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiAnonGet(`/groups-public?q=${encodeURIComponent(q || '')}`);
      if (res.success) setResults(res.data || []);
    } catch (e) {
      console.error('Search failed', e);
    } finally {
      setLoading(false);
    }
  }, [q]);

  // Auto-search when query length >= 3 with debounce, otherwise show top 25
  useEffect(() => {
    const trimmedQ = (q || '').trim();
    console.log('[GroupSearchPage] useEffect triggered, q:', q, 'trimmedQ:', trimmedQ, 'length:', trimmedQ.length);
    
    if (trimmedQ.length === 0) {
      // Load top 25 groups when query is empty
      (async () => {
        setLoading(true);
        try {
          console.log('[GroupSearchPage] Fetching top 25 groups...');
          const res = await apiAnonGet('/groups-public?limit=25&sort=members');
          console.log('[GroupSearchPage] Top groups response:', res);
          if (res.success && res.data) {
            console.log('[GroupSearchPage] Setting results:', res.data.length, 'groups');
            setResults(res.data);
          }
        } catch (e) {
          console.error('Failed to fetch top groups', e);
        } finally {
          setLoading(false);
        }
      })();
      return;
    }
    
    if (trimmedQ.length < 3) {
      // Don't search yet if between 1-2 characters
      console.log('[GroupSearchPage] Query too short, clearing results');
      setResults([]);
      return;
    }
    
    // Search with debounce for 3+ characters
    console.log('[GroupSearchPage] Setting up search debounce');
    const id = setTimeout(() => {
      void doSearch();
    }, 300);
    return () => clearTimeout(id);
  }, [q, doSearch]);

  return (
    <div className="space-y-4">
      <div>
        <Link to="/app/groups" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:underline">
          <ChevronLeft className="h-4 w-4" />
          <span>{t('groups.search.back', 'Tillbaka')}</span>
        </Link>
      </div>
      <div className="flex items-center gap-2">
        <Search className="h-6 w-6 text-primary" />
        <h1 className="text-2xl font-bold">{t('groups.search.title', 'Sök beredskapsgrupper')}</h1>
      </div>
      
      <p className="text-sm text-muted-foreground">
        Här kan du söka efter beredskapsgrupper som har markerats som publika av dess ägare. Du kan sedan ansöka om medlemskap. Gruppens ägare får en notifiering om din ansökan och kan sedan granska och godkänna den.
      </p>

      <h2 ref={searchHeadingRef} className="text-lg font-semibold mt-6">Sök grupp</h2>
      
      <div className="flex gap-2">
        <input maxLength={100} value={q} onChange={(e) => setQ(e.target.value)} onFocus={handleSearchFocus} className="flex-1 rounded-lg border border-border p-2" placeholder="Gruppnamn, ort eller beskrivning" />
        <button onClick={doSearch} className="px-3 py-2 rounded bg-[#67724e] text-white">{loading ? t('common.searching', 'Söker...') : t('common.search', 'Sök')}</button>
      </div>

      {/* Section heading */}
      <h2 className="text-lg font-semibold mt-6">
        {q.trim().length > 0 ? 'Sökresultat' : 'Utvalda'}
      </h2>

      <div className="space-y-3">
        {results.map((g) => {
          const id = String(g?.id || '');
          const name = String(g?.name || '');
          const desc = String(g?.description || '');
          const imageUrl = (g?.image as any)?.url || '';
          const city = g?.city ? String(g.city) : undefined;
          const memberCount = typeof g?.memberCount === 'number' ? g.memberCount : 0;
          const isMember = myGroupIds.has(id);
          const isPending = pendingGroupIds.has(id);
          const isFull = memberCount >= 500;
          console.log(`[GroupSearchPage] Rendering group ${name} (${id}): isMember=${isMember}, isPending=${isPending}, isFull=${isFull}`);
          return (
          <Link key={id} to={`/app/groups/${id}`} className="block rounded-lg border border-border p-3 bg-card hover:border-primary/50">
            <div className="flex items-center gap-3">
              {imageUrl ? (
                <img src={imageUrl} alt={name} className="h-12 w-12 rounded-md object-cover" />
              ) : (
                <div className="rounded-md bg-orange-500/10 border border-orange-500/60 p-2">
                  <Users className="h-6 w-6 text-orange-500" />
                </div>
              )}
              <div className="flex-1">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="font-semibold">{name}</h3>
                  {isMember && (
                    <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-50 border border-green-200 text-green-800 dark:bg-green-900/20 dark:border-green-700 dark:text-green-300 text-xs shrink-0">
                      <Check className="h-3 w-3" />
                      <span>Medlem</span>
                    </span>
                  )}
                  {!isMember && isPending && (
                    <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-50 border border-orange-200 text-orange-800 dark:bg-orange-900/20 dark:border-orange-700 dark:text-orange-300 text-xs shrink-0">
                      <Clock className="h-3 w-3" />
                      <span>Pågående ansökan</span>
                    </span>
                  )}
                  {!isMember && !isPending && isFull && (
                    <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-50 border border-gray-200 text-gray-800 dark:bg-gray-900/20 dark:border-gray-700 dark:text-gray-300 text-xs shrink-0">
                      <Users className="h-3 w-3" />
                      <span>Full</span>
                    </span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground line-clamp-2">{desc}</p>
                <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Users className="h-3 w-3" />
                    <span>{t('groups.memberCount', { count: memberCount })}</span>
                  </div>
                  {city && (
                    <>
                      <span>•</span>
                      <div className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        <span>{city}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </Link>
          );
        })}
      </div>
    </div>
  );
}
